import type { ReactNode } from 'react';
import { cn } from '../../../../lib/utils';
import { renderSummaryCellContent, resolveAlign } from '../column-types';
import { ALIGN_CLASS, DENSITY_CELL, type Density } from '../styles';
import type { DataGridColumn, DataGridSummaryItem } from '../types';

export interface GrandTotalRowProps<T> {
  columns: DataGridColumn<T>[];
  summary: DataGridSummaryItem<T>[];
  rows: T[];
  density: Density;
  currency: string;
  /** `true` when the scrollable container is capped (`maxHeight`) — the
   *  row then sticks to the bottom of that container via `position: sticky`
   *  on each cell. ExcelJS quirk aside, sticky on `<tr>` / `<tfoot>` is
   *  unreliable across browsers, hence the per-cell application. */
  sticky: boolean;
}

/**
 * Grand total row rendered inside the grid's `<tfoot>`. Any non-summary
 * column on the left falls back to a plain "Total" anchor label so the
 * row always has a visible marker.
 */
export function GrandTotalRow<T>({
  columns,
  summary,
  rows,
  density,
  currency,
  sticky,
}: GrandTotalRowProps<T>): ReactNode {
  return (
    <tfoot>
      <tr className="border-t-2 border-neutral-300 bg-neutral-100 font-semibold">
        {columns.map((col) => (
          <td
            key={col.id}
            className={cn(
              'align-middle text-dark-500 bg-neutral-100',
              DENSITY_CELL[density],
              ALIGN_CLASS[resolveAlign(col)],
              col.cellClassName,
              sticky && 'sticky bottom-0 z-10'
            )}
          >
            {renderSummaryCellContent(col, summary, rows, currency) ??
              (columns[0] === col ? <span>Total</span> : null)}
          </td>
        ))}
      </tr>
    </tfoot>
  );
}
